/**
 * Dual-mode named hook for refinement run subscriptions (FR-004).
 *
 * List mode (no argument): returns RefinementRunListResult with all runs
 * transformed into RefinementRunCard view-models.
 *
 * Detail mode (with runId): returns RefinementRunDetailResult with the
 * composite output (run + passes + convergence + artifacts) transformed
 * into view-model types.
 *
 * Both useQuery calls are unconditional (Rules of Hooks). When
 * useSubscriptionActive() returns false, the literal "skip" is passed
 * to useQuery to prevent a network round-trip.
 *
 * No inline kindLabel ternary or hardcoded terminal-status array literal
 * appears in this file — isTerminal and kindLabel are computed in the
 * transform layer (transformWorkflowRun, transformWorkflowPass).
 */
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import type { Id } from "../../../../../convex/_generated/dataModel";
import { useSubscriptionActive } from "../../components/layout/SubscriptionScope";
import { useEffectiveUser } from "./useEffectiveUser";
import { transformWorkflowRun } from "../transforms/workflowRun";
import { transformWorkflowPass } from "../transforms/workflowPass";
import type {
  RefinementRunListResult,
  RefinementRunDetailResult,
  RefinementRunDetail,
  ConvergencePoint,
  ArtifactEntry,
} from "../types/refinementRun";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Maximum number of rows requested in list-mode subscriptions.
 * Bounds the server-side payload to prevent unbounded wire traffic.
 */
const MAX_ROWS = 200;

// ---------------------------------------------------------------------------
// Function overloads
// ---------------------------------------------------------------------------

/**
 * List mode — returns all workflow runs as RefinementRunCard[].
 */
export function useRefinementRun(): RefinementRunListResult;

/**
 * Detail mode — returns a single run with passes, convergence, and artifacts.
 */
export function useRefinementRun(
  runId: Id<"workflowRuns">,
): RefinementRunDetailResult;

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export function useRefinementRun(
  runId?: Id<"workflowRuns">,
): RefinementRunListResult | RefinementRunDetailResult {
  const isActive = useSubscriptionActive();
  const me = useEffectiveUser();

  // ── List-mode subscription (unconditional — Rules of Hooks) ────────
  // subscribeToWorkflowRunsByUser requires userId. Skip the query until
  // useEffectiveUser resolves; the consumer renders "loading" in that
  // window. Once we have the user, pass their Id explicitly so the server
  // validator is satisfied (previously the call was made with `{ limit }`
  // only, which the validator rejected at runtime).
  const listResult = useQuery(
    api.sessions.queries.subscribeToWorkflowRunsByUser,
    isActive && runId === undefined && me !== null
      ? { limit: MAX_ROWS, userId: me._id as Id<"users"> }
      : "skip",
  );

  // ── Detail-mode subscription (unconditional — Rules of Hooks) ──────
  const detailResult = useQuery(
    api.workflowRuns.getRun,
    isActive && runId !== undefined ? { runId } : "skip",
  );

  // ── List mode ──────────────────────────────────────────────────────
  if (runId === undefined) {
    if (!isActive || listResult === undefined) {
      return { status: "loading" };
    }

    return {
      status: "ready",
      data: listResult.map(transformWorkflowRun),
    };
  }

  // ── Detail mode ────────────────────────────────────────────────────
  if (!isActive || detailResult === undefined) {
    return { status: "loading" };
  }

  if (detailResult === null) {
    return { status: "not_found" };
  }

  const run = transformWorkflowRun(detailResult.run);

  const passes = detailResult.passes.map(
    (p: (typeof detailResult.passes)[number]) =>
      transformWorkflowPass(
        p,
        run.contentThreshold,
        run.structuralThreshold,
        undefined,
        undefined,
      ),
  );

  const convergence: ConvergencePoint[] = detailResult.convergence.map(
    (c: (typeof detailResult.convergence)[number]) => ({
      id: c._id,
      passIndex: c.passIndex ?? 0,
      contentDelta: c.contentDelta ?? 0,
      structuralDelta: c.structuralDelta ?? 0,
      converged: c.belowThreshold ?? false,
      oscillating: (c as Record<string, unknown>)["oscillating"] === true,
      recordedAt: c.recordedAt ?? 0,
    }),
  );

  const artifacts: ArtifactEntry[] = detailResult.artifacts.map(
    (a: (typeof detailResult.artifacts)[number]) => ({
      id: a._id,
      passIndex: a.passIndex ?? 0,
      task: (a as Record<string, unknown>)["task"] as string ?? a.name ?? "",
      uri: a.r2Uri ?? "",
      sha: (a as Record<string, unknown>)["sha"] as string ?? "",
      bytes: a.sizeBytes ?? 0,
      createdAt: a._creationTime,
    }),
  );

  const detail: RefinementRunDetail = {
    run,
    passes,
    convergence,
    artifacts,
  };

  return { status: "ready", data: detail };
}
